import React from 'react';
import { useIntl } from '@kyruus/intl';
import { PMCConfig } from '@kyruus/types';

import { DEFAULT_LOCALE, getConfigProperty } from 'Common/config';
import { MarketableLocation } from 'Src/types';
import {
  getDisplayAddress,
  buildLocationCanonicalURL,
  getHeroImage
} from 'Src/utils/location';
import { messages } from 'Src/shared/messages';
import { IntlHelmet } from 'Src/utils/intl-helmet';

interface MetaProps {
  config: PMCConfig;
  marketableLocation: MarketableLocation;
}

export const Meta = ({ config, marketableLocation }: MetaProps) => {
  const intl = useIntl();

  const {
    name,
    address,
    specialties = [],
    services = [],
    media,
    location_type
  } = marketableLocation;

  const locale = getConfigProperty(config, 'locale', DEFAULT_LOCALE);
  const primaryDomain = getConfigProperty(config, 'primary_domain');
  const host = getConfigProperty(config, 'host');

  const specialtyNames =
    specialties.map(({ specialty }) => specialty).join(', ') || '';
  const serviceNames = services.map(({ name }) => name).join(', ') || '';

  const title = intl.formatMessage(messages.metaLocationTitle, {
    name,
    city: address.city,
    state: address.state,
    locationType: location_type?.name
  });
  const descriptionMessage = intl.formatMessage(
    messages.metaLocationDescription,
    {
      name,
      address: getDisplayAddress(address),
      locationType: location_type?.name,
      serviceNames,
      specialtyNames
    }
  );
  const url = buildLocationCanonicalURL(
    primaryDomain || host,
    marketableLocation
  );
  const heroImage = getHeroImage(media);

  const meta = [
    { name: 'description', content: descriptionMessage },
    { property: 'og:title', content: title },
    { property: 'og:locale', content: locale },
    { property: 'og:description', content: descriptionMessage },
    { name: 'url', property: 'og:url', itemprop: 'url', content: url },
    { property: 'og:type', content: 'profile' },
    ...(heroImage ? [{ property: 'og:image', content: heroImage }] : [])
  ];

  return (
    <IntlHelmet
      title={title}
      meta={meta}
      link={[{ rel: 'canonical', href: url }]}
    />
  );
};
